import {
	IconCheck,
	IconCopy,
	IconEye,
	IconEyeOff,
	IconX,
} from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type CopyState = "idle" | "copied" | "failed";

const MASK = "••••••••••••";

export const ApiKeyValue = ({ value }: { value: string }) => {
	const { t } = useTranslation();
	const [revealed, setRevealed] = useState(false);
	const [copyState, setCopyState] = useState<CopyState>("idle");
	const resetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (resetTimeout.current) {
				clearTimeout(resetTimeout.current);
			}
		},
		[],
	);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopyState("copied");
		} catch {
			setCopyState("failed");
		}
		if (resetTimeout.current) {
			clearTimeout(resetTimeout.current);
		}
		resetTimeout.current = setTimeout(() => setCopyState("idle"), 1500);
	};

	const copyIcon = {
		idle: <IconCopy size={16} className="text-gray-400" />,
		copied: <IconCheck size={16} className="text-green-600" />,
		failed: <IconX size={16} className="text-red-500" />,
	}[copyState];

	const copyLabel = {
		idle: t("Copy"),
		copied: t("Copied"),
		failed: t("Copy failed"),
	}[copyState];

	return (
		<div className="inline-flex items-center gap-2">
			<span className="font-mono text-sm">{revealed ? value : MASK}</span>
			<button
				type="button"
				onClick={() => setRevealed((prev) => !prev)}
				aria-label={revealed ? t("Hide") : t("Show")}
				aria-pressed={revealed}
				className="rounded p-1 text-gray-500 hover:bg-gray-100"
			>
				{revealed ? <IconEyeOff size={16} /> : <IconEye size={16} />}
			</button>
			<button
				type="button"
				onClick={handleCopy}
				aria-label={copyLabel}
				className="rounded p-1 hover:bg-gray-100"
			>
				{copyIcon}
			</button>
			<span className="sr-only" role="status" aria-live="polite">
				{copyState !== "idle" ? copyLabel : ""}
			</span>
		</div>
	);
};
